// pages/Register/CompanyInfoPage.tsx
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Building2,
    Globe,
    MapPin,
    Briefcase,
    Users,
    ChevronRight,
    ArrowLeft,
    Check,
    AlertCircle,
    Flag,
    BookOpen
} from "lucide-react";
import { BaseEntreprise } from "../../types/features/Entreprise";

const CompanyInfoPage = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<Partial<BaseEntreprise>>({
        name: "",
        address: "",
        pays: "",
        website: "",
        nature_juridique: "",
        taille: "",
        secteur_activity: "",
        subscription_plan: "starter",
        subscription_status: "trial"
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };
        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name?.trim()) {
            newErrors.name = "Le nom de l'entreprise est obligatoire";
        }
        if (!formData.address?.trim()) {
            newErrors.address = "L'adresse est obligatoire";
        }
        if (!formData.pays?.trim()) {
            newErrors.pays = "Le pays est obligatoire";
        }

        if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
            newErrors.website = "URL invalide (ex: https://monentreprise.com)";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setIsLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            console.log("Données entreprise :", formData);
            navigate('/checkout');
        } catch (error) {
            console.error("Erreur :", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen bg-white font-sans antialiased select-none">

            {/* ========== PANEL GAUCHE ========== */}
            <div className="relative hidden w-1/2 bg-[#031B4E] lg:block overflow-hidden">
                {/* Trame de fond */}
                <div
                    className="absolute inset-0 opacity-5"
                    style={{
                        backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
                        backgroundSize: "32px 32px"
                    }}
                />

                {/* Effet lumineux dynamique */}
                <div
                    className="absolute w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl pointer-events-none transition-all duration-500 ease-out"
                    style={{
                        top: `${mousePosition.y - 300}px`,
                        left: `${mousePosition.x - 300}px`,
                    }}
                />
                <div className="relative flex h-full flex-col justify-between p-16 text-white">
                    {/* Header Logo */}
                    <Link to={"/"} className="flex items-center gap-2">
                        <img src="/logo.png" alt="IZI Call Center" className="h-12 w-auto" />
                        <div>
                            <div className="text-xl font-black tracking-wider leading-none">
                                <span className="text-white">Izi</span> <span className="text-[#FFB800]">CALL CENTER</span>
                            </div>
                            <div className="text-[9px] tracking-[0.25em] text-gray-300 font-medium uppercase mt-0.5">
                                Connecter • Assister • Satisfaire
                            </div>
                        </div>
                    </Link>
                    {/* Contenu principal */}
                    <div className="flex-1 flex items-start pt-12">
                        <div className="max-w-xl space-y-8">
                            <div className="space-y-4">
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-medium text-blue-300 border border-blue-500/20">
                                    Étape 1 sur 2
                                </span>
                                <h1 className="text-5xl font-extrabold tracking-tight leading-[1.15] text-white">
                                    Configurez votre <br />
                                    <span className="text-blue-400">espace entreprise</span>
                                </h1>
                                <p className="text-lg text-slate-300/90 leading-relaxed font-light">
                                    Renseignez les informations de votre structure pour finaliser votre inscription.
                                </p>
                            </div>

                            {/* Étapes de progression */}
                            <div className="space-y-4 pt-4 border-t border-white/5">
                                <div className="flex items-start gap-4">
                                    <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/5 border border-white/10 text-blue-400">
                                        <Building2 className="h-3.5 w-3.5" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-300 font-medium">Informations générales</p>
                                        <p className="text-xs text-slate-400">Nom, adresse, pays</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/5 border border-white/10 text-blue-400">
                                        <Briefcase className="h-3.5 w-3.5" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-300 font-medium">Informations complémentaires</p>
                                        <p className="text-xs text-slate-400">Secteur, taille, forme juridique</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-md bg-white/5 border border-white/10 text-blue-400">
                                        <Check className="h-3.5 w-3.5" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-300 font-medium">Finalisation</p>
                                        <p className="text-xs text-slate-400">Choix de l'offre et paiement</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ========== FOOTER CORRIGÉ ========== */}
                    <div className="flex items-center justify-between text-xs border-t border-white/5 pt-6">
                        <div className="flex items-center gap-4">
                            <Link
                                to="/mentions-legales"
                                className="text-slate-400/70 hover:text-white transition-colors"
                            >
                                Mentions légales
                            </Link>
                            <span className="text-slate-500/30">•</span>
                            <Link
                                to="/confidentialite"
                                className="text-slate-400/70 hover:text-white transition-colors"
                            >
                                Confidentialité
                            </Link>
                            <span className="text-slate-500/30">•</span>
                            <Link
                                to="/cgu"
                                className="text-slate-400/70 hover:text-white transition-colors"
                            >
                                CGU
                            </Link>
                        </div>
                        <span className="text-slate-500/50">
                            © 2026
                        </span>
                    </div>
                </div>
            </div>

            {/* ========== PANEL DROIT ========== */}
            <div className="flex w-full flex-col justify-between px-6 py-10 lg:w-1/2 sm:px-16 lg:px-24 bg-white">

                {/* Logo mobile */}
                <div className="flex items-center justify-between lg:hidden mb-8">
                    <div className="flex items-center gap-2.5">
                        <img src="/logo.png" alt="Izi CallCenter" className="h-8 w-auto" />
                        <span className="text-lg font-bold text-[#031B4E]">Izi CallCenter</span>
                    </div>
                </div>

                {/* Spacer pour centrer verticalement en desktop */}
                <div className="hidden lg:block h-8" />

                {/* Formulaire */}
                <div className="mx-auto w-full max-w-md space-y-8 my-auto">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Building2 className="h-5 w-5 text-blue-600" />
                            <h2 className="text-3xl font-bold tracking-tight text-[#031B4E]">
                                Votre entreprise
                            </h2>
                        </div>
                        <p className="text-sm text-slate-500 font-light">
                            Complétez ces informations pour activer votre compte.
                        </p>
                    </div>

                    <form className="space-y-5" onSubmit={handleSubmit}>

                        {/* SECTION OBLIGATOIRE */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                                <div className="h-5 w-1 bg-blue-600 rounded-full"></div>
                                <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                                    Informations obligatoires
                                </span>
                                <span className="text-[10px] text-red-500 ml-auto">*</span>
                            </div>

                            {/* Nom */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                                    Nom de l'entreprise <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={`w-full rounded-xl border ${errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50/50'
                                            } py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all`}
                                        placeholder="Ma Super Entreprise SAS"
                                    />
                                </div>
                                {errors.name && (
                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3" />
                                        {errors.name}
                                    </p>
                                )}
                            </div>

                            {/* Adresse */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                                    Adresse <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        className={`w-full rounded-xl border ${errors.address ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50/50'
                                            } py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all`}
                                        placeholder="123 rue de l'Exemple, 75000 Paris"
                                    />
                                </div>
                                {errors.address && (
                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3" />
                                        {errors.address}
                                    </p>
                                )}
                            </div>

                            {/* Pays */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                                    Pays <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Flag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <select
                                        name="pays"
                                        value={formData.pays}
                                        onChange={handleChange}
                                        className={`w-full rounded-xl border ${errors.pays ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50/50'
                                            } py-3 pl-10 pr-4 text-sm text-slate-900 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all appearance-none`}
                                    >
                                        <option value="">Sélectionnez votre pays</option>
                                        <option value="France">France</option>
                                        <option value="Belgique">Belgique</option>
                                        <option value="Suisse">Suisse</option>
                                        <option value="Canada">Canada</option>
                                        <option value="Luxembourg">Luxembourg</option>
                                        <option value="Côte d'Ivoire">Côte d'Ivoire</option>
                                        <option value="Sénégal">Sénégal</option>
                                        <option value="Cameroun">Cameroun</option>
                                        <option value="Maroc">Maroc</option>
                                        <option value="Tunisie">Tunisie</option>
                                        <option value="Algérie">Algérie</option>
                                        <option value="Autre">Autre</option>
                                    </select>
                                </div>
                                {errors.pays && (
                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3" />
                                        {errors.pays}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* SECTION FACULTATIVE */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                                <div className="h-5 w-1 bg-slate-300 rounded-full"></div>
                                <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                                    Informations complémentaires
                                </span>
                                <span className="text-[10px] text-slate-400 ml-auto">Optionnel</span>
                            </div>

                            {/* Site web */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-700">
                                    Site web
                                </label>
                                <div className="relative">
                                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <input
                                        type="url"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleChange}
                                        className={`w-full rounded-xl border ${errors.website ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50/50'
                                            } py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all`}
                                        placeholder="https://monentreprise.com"
                                    />
                                </div>
                                {errors.website && (
                                    <p className="text-xs text-red-500 flex items-center gap-1">
                                        <AlertCircle className="h-3 w-3" />
                                        {errors.website}
                                    </p>
                                )}
                            </div>

                            {/* Nature juridique + Taille */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-700">
                                        Nature juridique
                                    </label>
                                    <div className="relative">
                                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <select
                                            name="nature_juridique"
                                            value={formData.nature_juridique}
                                            onChange={handleChange}
                                            className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-slate-900 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all appearance-none"
                                        >
                                            <option value="">Sélectionnez</option>
                                            <option value="SAS">SAS</option>
                                            <option value="SARL">SARL</option>
                                            <option value="EURL">EURL</option>
                                            <option value="SA">SA</option>
                                            <option value="EI">Entreprise Individuelle</option>
                                            <option value="Association">Association</option>
                                            <option value="Autre">Autre</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-700">
                                        Taille
                                    </label>
                                    <div className="relative">
                                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <select
                                            name="taille"
                                            value={formData.taille}
                                            onChange={handleChange}
                                            className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-slate-900 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all appearance-none"
                                        >
                                            <option value="">Sélectionnez</option>
                                            <option value="1-5">1-5 employés</option>
                                            <option value="6-20">6-20 employés</option>
                                            <option value="21-50">21-50 employés</option>
                                            <option value="51-200">51-200 employés</option>
                                            <option value="201+">201+ employés</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Secteur d'activité */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-slate-700">
                                    Secteur d'activité
                                </label>
                                <div className="relative">
                                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <input
                                        type="text"
                                        name="secteur_activity"
                                        value={formData.secteur_activity}
                                        onChange={handleChange}
                                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 py-3 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                                        placeholder="Services, Commerce, Tech, Santé..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Récapitulatif */}
                        <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
                            <div className="flex items-start gap-3">
                                <Check className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-blue-800">
                                        Les champs marqués d'un <span className="text-red-500">*</span> sont obligatoires
                                    </p>
                                    <p className="text-xs text-blue-600/80 mt-0.5">
                                        Ces informations nous permettront de configurer votre espace entreprise.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Bouton */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#031B4E] px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/10 hover:bg-[#052975] focus:outline-none focus:ring-4 focus:ring-blue-600/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center">
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span className="ml-2 font-medium">Enregistrement...</span>
                                </div>
                            ) : (
                                <>
                                    <span>Découvrir nos forfait</span>
                                    <ChevronRight className="h-4 w-4" />
                                </>
                            )}
                        </button>

                        {/* Lien retour */}
                        <div className="text-center pt-2">
                            <Link
                                to="/register"
                                className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-[#031B4E] transition-colors gap-1"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                                Retour à l'inscription
                            </Link>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="text-center text-xs text-slate-400 mt-8">
                    © 2026 Izi CallCenter. Tous droits réservés.
                </div>
            </div>
        </div>
    );
};

export default CompanyInfoPage;